import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { hueVars, slugify } from "../data/artists";
import SiteFooter from "./SiteFooter";
import PeriodTimeline from "./PeriodTimeline";
import GalleryWallZoom from "./GalleryWallZoom";
import thresholdTexture from "../assets/threshold-texture.webp";

const EASE = [0.22, 1, 0.36, 1];

export default function ArtistWorld({ artist, index, total, onBack, onNext, onViewPiece }) {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const foilY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const copyY = useTransform(scrollYProgress, [0, 1], ["0%", "-22%"]);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
    window.scrollTo(0, 0);
  }, [artist.name]);

  const nameLines = artist.name.split(" ");
  const [copied, setCopied] = useState(false);

  useEffect(() => setCopied(false), [artist.name]);

  const shareWorld = async () => {
    const url = `${window.location.origin}${window.location.pathname}#/world/${slugify(artist.name)}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  /* chapters: Practice leads, then Origins, then The Collaboration */
  const orderBeats = (beats) =>
    [...beats].sort((a, b) => (b.label === "Practice" ? 1 : 0) - (a.label === "Practice" ? 1 : 0));

  const renderBeat = (b, idx) => (    <motion.div
      className={`beat${idx === 1 && artist.photos && artist.photos.length > 0 ? " beat-has-bg" : ""}`}
      key={b.label}
      data-testid={`beat-${idx + 1}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.9, ease: EASE }}
    >
      {idx === 1 && artist.photos && artist.photos.length > 0 && (
        <div
          className="beat-bg"
          style={{ backgroundImage: `url(${artist.photos[1] || artist.photos[0]})` }}
          aria-hidden="true"
        />
      )}
      <div className="num">
        {String(idx + 1).padStart(2, "0")}
        <span className="label">{b.label}</span>
      </div>
      <div>
        {b.text.split("\n\n").map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </div>
    </motion.div>
  );

  return (
    <section id="artist-page" data-testid="artist-world">
      <div className="back-bar">
        <button onClick={onBack} data-testid="back-to-hall-button">← The Hall</button>
        <div className="bb-right">
          <button className="share-btn" data-testid="share-world-button" onClick={shareWorld}>
            {copied ? "Copied" : "Copy link"}
          </button>
          <button className="mark" data-testid="brand-to-hall-button" onClick={onBack} aria-label="Back to The Hall">
            The WOWOW Gallery
          </button>
        </div>
      </div>

      <div className="a-hero" ref={heroRef}>
        <motion.div
          className="a-hero-texture"
          style={{
            backgroundImage: `url(${thresholdTexture})`,
            filter: `brightness(0.6) saturate(0.85) hue-rotate(${((artist.hue - 185) + 360) % 360}deg)`,
            y: foilY,
            scale: 1.2,
          }}
          aria-hidden="true"
        />
        <div className="a-hero-tint" style={hueVars(artist.hue)} aria-hidden="true" />
        {artist.photos && artist.photos.length > 0 && (
          <motion.div
            className="a-hero-photo"
            data-testid="hero-photo"
            style={{ backgroundImage: `url(${artist.photos[0]})`, y: foilY }}
            aria-hidden="true"
          />
        )}
        <div className="a-hero-shade" aria-hidden="true" />
        <motion.div className="a-hero-copy" style={{ y: copyY }}>
          <div className="eyebrow">The world of — Door {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</div>
          <h2 className="display" data-testid="artist-hero-name">
            {nameLines.map((line, i) => (
              <span className="mask-line" key={i}>
                <motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1.05, delay: 0.2 + i * 0.13, ease: EASE }}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h2>
        </motion.div>
        <div className="scroll-cue">Scroll ↓</div>
      </div>

      {artist.comingSoon ? (
        <motion.div
          className="coming-soon"
          data-testid="coming-soon"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <div className="eyebrow">Coming soon</div>
          <p>This world is still being made. The door is hung; the room behind it is in progress.</p>
        </motion.div>
      ) : artist.periods ? (
        <>
          <div className="tl-intro">
            <div className="eyebrow">{artist.timelineEyebrow || "The Timeline"}</div>
            {(artist.timelineIntro || "One studio, six centuries — skip through the periods we've transformed.")
              .split("\n\n")
              .map((para, i) => (
                <p key={i}>{para}</p>
              ))}
          </div>
          <PeriodTimeline artist={artist} onViewPiece={onViewPiece} />
          <div className="a-footer">
            <p>Every transformation is a limited edition, from WOW Studio's own production. Enquiries are handled privately.</p>
            <button className="next-btn" data-testid="next-artist-button" onClick={onNext}>Next world →</button>
          </div>
        </>
      ) : (
        <>
      <div className={artist.wallStyle === "dark" ? undefined : "gw-light"}>
      <div className="gw-head">
        <h3 className="display" data-testid="piece-title">The Pieces</h3>
      </div>
      <GalleryWallZoom artist={artist} />
      </div>

          {orderBeats(artist.beats).map((b, i) => renderBeat(b, i))}

      <div className="a-footer">
        <p>Every piece in the collection is a limited edition, made in direct collaboration with the artist. Enquiries are handled privately.</p>
        <button className="next-btn" data-testid="next-artist-button" onClick={onNext}>Next world →</button>
      </div>
        </>
      )}

      <SiteFooter />
    </section>
  );
}
